import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Plus, Calendar, Zap, Trophy, ChevronDown, Repeat, MessageCircle, Users, User, Swords, Heart, Check, Clock, X, Camera } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { PageContainer } from '@/components/layout'
import { CharacterDisplay } from '@/components/character'
import { Card, Button, Modal, EmptyState, EMPTY_STATES } from '@/components/ui'
import { MilestoneCelebration } from '@/components/MilestoneCelebration'
import { useTaskStore } from '@/stores/useTaskStore'
import { useUserStore, type XpGainResult } from '@/stores/useUserStore'
import { useCharacterStore } from '@/stores/useCharacterStore'
import { useEmoteStore } from '@/stores/useEmoteStore'
import { usePartnershipStore } from '@/stores/usePartnershipStore'
import { getCurrentUserId } from '@/lib/sync'
import { DIFFICULTY_XP, TASK_CATEGORIES, type Task, type Difficulty, type TaskCategory, type RecurrenceType, type RecurrencePattern } from '@/types'
import { CHARACTER_DIALOGUES } from '@/lib/constants'
import { useSounds } from '@/lib/sounds'
import { formatDistanceToNow } from '@/lib/utils'
import { getAffectionLevel, formatAffection } from '@/lib/affection'
import type { MilestonePercent } from '@/lib/milestones'
import type { PartnerQuest, PartnershipWithDetails } from '@/types/social'

export function Home() {
  const navigate = useNavigate()
  const { tasks, completeTask, addTask } = useTaskStore()
  const { addXp, activeCharacterId, streakDays } = useUserStore()
  const { addAffection } = useCharacterStore()
  const { playComplete, playSuccess, playMilestone } = useSounds()
  const { getUnreadCount, fetchEmoteMessages, subscribeToEmotes } = useEmoteStore()
  const {
    waifuMode,
    fetchPartnerships,
    getAllPartnershipsWithDetails,
    fetchPartnerQuests,
    getQuestsAssignedToMe,
    getQuestsAssignedByMe,
    updateQuestStatus,
    createPartnerQuest,
    completeQuestWithProof,
  } = usePartnershipStore()

  // View mode: 'solo' for personal quests, 'partner' for partner quests
  const [viewMode, setViewMode] = useState<'solo' | 'partner'>('solo')
  const [partnerships, setPartnerships] = useState<PartnershipWithDetails[]>([])
  const [selectedPartner, setSelectedPartner] = useState<PartnershipWithDetails | null>(null)

  // Fetch emotes if in social mode
  useEffect(() => {
    if (waifuMode === 'social') {
      fetchEmoteMessages()
      const unsubscribe = subscribeToEmotes()
      return () => unsubscribe()
    }
  }, [waifuMode, fetchEmoteMessages, subscribeToEmotes])

  // Fetch partnerships and partner quests when in social mode
  useEffect(() => {
    if (waifuMode === 'social') {
      const loadData = async () => {
        await fetchPartnerships()
        await fetchPartnerQuests()
        const details = await getAllPartnershipsWithDetails()
        setPartnerships(details.filter(p => p.status === 'active'))
        if (details.length > 0 && !selectedPartner) {
          setSelectedPartner(details.find(p => p.status === 'active') || null)
        }
      }
      loadData()
    }
  }, [waifuMode, fetchPartnerships, fetchPartnerQuests, getAllPartnershipsWithDetails, selectedPartner])

  const unreadEmotes = waifuMode === 'social' ? getUnreadCount() : 0

  // Partner quests data
  const questsForMe = getQuestsAssignedToMe()
  const questsByMe = getQuestsAssignedByMe()
  const activeQuestsForMe = questsForMe.filter(q => ['pending', 'accepted'].includes(q.status)).slice(0, 5)
  const pendingVerification = questsByMe.filter(q => q.status === 'completed').slice(0, 3)

  // Partner quest completion modal
  const [questToComplete, setQuestToComplete] = useState<PartnerQuest | null>(null)
  const [proofFile, setProofFile] = useState<File | null>(null)
  const [proofPreview, setProofPreview] = useState<string | null>(null)
  const [isCompletingQuest, setIsCompletingQuest] = useState(false)
  const proofInputRef = useRef<HTMLInputElement>(null)

  // Partner quest creation modal
  const [showCreateQuest, setShowCreateQuest] = useState(false)
  const [newQuestTitle, setNewQuestTitle] = useState('')
  const [newQuestDescription, setNewQuestDescription] = useState('')
  const [newQuestEmoji, setNewQuestEmoji] = useState('⭐')
  const [newQuestXp, setNewQuestXp] = useState(25)
  const [newQuestReward, setNewQuestReward] = useState('')
  const [newQuestStake, setNewQuestStake] = useState('')
  const [newQuestDueDate, setNewQuestDueDate] = useState('')
  const [showQuestOptions, setShowQuestOptions] = useState(false)
  const [isCreatingQuest, setIsCreatingQuest] = useState(false)

  const QUEST_EMOJIS = ['⭐', '💪', '📚', '🏃', '🧹', '💝', '🎯', '✨', '🔥', '💫', '🏆', '⚔️']
  const QUICK_REWARDS = [
    { label: 'Head pats', emoji: '🥰' },
    { label: 'Dinner date', emoji: '🍽️' },
    { label: 'Movie night', emoji: '🎬' },
    { label: 'Massage', emoji: '💆' },
    { label: 'Surprise', emoji: '🎁' },
  ]

  // Completion modal state
  const [completionModal, setCompletionModal] = useState<{
    show: boolean
    task: Task | null
    xpGained: number
  }>({ show: false, task: null, xpGained: 0 })

  // Milestone celebration state
  const [milestoneModal, setMilestoneModal] = useState<{
    show: boolean
    milestone: MilestonePercent | null
    gemReward: number
    affectionBonus: number
    newLevel: number | undefined
  }>({ show: false, milestone: null, gemReward: 0, affectionBonus: 0, newLevel: undefined })

  // Pending milestone to show after task completion closes
  const [pendingMilestone, setPendingMilestone] = useState<XpGainResult | null>(null)

  const [showAddTask, setShowAddTask] = useState(false)
  const [newTaskTitle, setNewTaskTitle] = useState('')
  const [newTaskDifficulty, setNewTaskDifficulty] = useState<Difficulty>('easy')

  // Expanded options state
  const [showMoreOptions, setShowMoreOptions] = useState(false)
  const [newDescription, setNewDescription] = useState('')
  const [newCategory, setNewCategory] = useState<TaskCategory | null>(null)
  const [newDueDate, setNewDueDate] = useState('')
  const [newDueTime, setNewDueTime] = useState('')
  const [isRecurring, setIsRecurring] = useState(false)
  const [recurrenceType, setRecurrenceType] = useState<RecurrenceType>('daily')
  const [weeklyDays, setWeeklyDays] = useState<number[]>([])
  const [monthlyDay, setMonthlyDay] = useState<number>(1)
  const [yearlyMonth, setYearlyMonth] = useState<number>(0)

  const WEEKDAYS = [
    { value: 0, label: 'S' },
    { value: 1, label: 'M' },
    { value: 2, label: 'T' },
    { value: 3, label: 'W' },
    { value: 4, label: 'T' },
    { value: 5, label: 'F' },
    { value: 6, label: 'S' },
  ]

  const MONTHS = [
    { value: 0, label: 'Jan' }, { value: 1, label: 'Feb' }, { value: 2, label: 'Mar' },
    { value: 3, label: 'Apr' }, { value: 4, label: 'May' }, { value: 5, label: 'Jun' },
    { value: 6, label: 'Jul' }, { value: 7, label: 'Aug' }, { value: 8, label: 'Sep' },
    { value: 9, label: 'Oct' }, { value: 10, label: 'Nov' }, { value: 11, label: 'Dec' },
  ]

  const todaysTasks = tasks.filter(t => !t.completed).slice(0, 5)

  const handleCompleteTask = (taskId: string) => {
    const task = completeTask(taskId)
    if (task) {
      const result = addXp(task.xpReward)
      const baseAffection = Math.floor(task.xpReward / 5)
      addAffection(activeCharacterId, baseAffection + result.affectionBonus)

      // Play appropriate sound
      if (result.milestoneCrossed) {
        playMilestone()
        setPendingMilestone(result)
      } else {
        playComplete()
      }

      setCompletionModal({
        show: true,
        task,
        xpGained: task.xpReward,
      })
    }
  }

  const handleCompletionClose = () => {
    setCompletionModal({ show: false, task: null, xpGained: 0 })

    // Show milestone celebration if there's a pending one
    if (pendingMilestone && pendingMilestone.milestoneCrossed) {
      setMilestoneModal({
        show: true,
        milestone: pendingMilestone.milestoneCrossed,
        gemReward: pendingMilestone.gemReward,
        affectionBonus: pendingMilestone.affectionBonus,
        newLevel: pendingMilestone.leveledUp ? pendingMilestone.newLevel : undefined,
      })
      setPendingMilestone(null)
    }
  }

  const handleAddTask = () => {
    if (!newTaskTitle.trim()) return

    // Build recurrence pattern if recurring
    let recurrencePattern: RecurrencePattern | null = null
    if (isRecurring) {
      recurrencePattern = {
        type: recurrenceType,
        days: recurrenceType === 'weekly' ? weeklyDays : undefined,
        dayOfMonth: (recurrenceType === 'monthly' || recurrenceType === 'yearly') ? monthlyDay : undefined,
        month: recurrenceType === 'yearly' ? yearlyMonth : undefined,
      }
    }

    addTask({
      title: newTaskTitle.trim(),
      description: newDescription.trim() || null,
      difficulty: newTaskDifficulty,
      category: newCategory,
      xpReward: Math.floor(
        Math.random() * (DIFFICULTY_XP[newTaskDifficulty].max - DIFFICULTY_XP[newTaskDifficulty].min + 1)
      ) + DIFFICULTY_XP[newTaskDifficulty].min,
      dueDate: isRecurring ? new Date().toISOString() : (newDueDate || null),
      dueTime: newDueTime || null,
      isRecurring,
      recurrencePattern,
    })

    playSuccess()
    // Reset all form state
    setNewTaskTitle('')
    setNewTaskDifficulty('easy')
    setShowMoreOptions(false)
    setNewDescription('')
    setNewCategory(null)
    setNewDueDate('')
    setNewDueTime('')
    setIsRecurring(false)
    setRecurrenceType('daily')
    setWeeklyDays([])
    setMonthlyDay(1)
    setYearlyMonth(0)
    setShowAddTask(false)
  }

  const getDifficultyColor = (difficulty: Difficulty) => {
    switch (difficulty) {
      case 'easy': return 'text-success'
      case 'medium': return 'text-accent'
      case 'hard': return 'text-warning'
      case 'boss': return 'text-danger'
    }
  }

  const getDifficultyIcon = (difficulty: Difficulty) => {
    switch (difficulty) {
      case 'easy': return '🌟'
      case 'medium': return '⚔️'
      case 'hard': return '🔥'
      case 'boss': return '👹'
    }
  }

  // Handle proof file selection
  const handleProofSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validate file
      const maxSize = 50 * 1024 * 1024 // 50MB
      if (file.size > maxSize) {
        alert('File too large (max 50MB)')
        return
      }
      setProofFile(file)
      // Create preview
      const url = URL.createObjectURL(file)
      setProofPreview(url)
    }
  }

  // Handle partner quest completion
  const handleCompletePartnerQuest = async () => {
    if (!questToComplete) return

    setIsCompletingQuest(true)
    try {
      if (proofFile) {
        // Complete with proof
        const result = await completeQuestWithProof(questToComplete.id, proofFile)
        if (!result.success) {
          alert(result.error || 'Failed to complete quest')
          setIsCompletingQuest(false)
          return
        }
      } else {
        // Complete without proof
        await updateQuestStatus(questToComplete.id, 'completed')
      }

      playSuccess()
      // Reset and close
      setQuestToComplete(null)
      setProofFile(null)
      setProofPreview(null)
      // Refresh quests
      await fetchPartnerQuests()
    } catch (error) {
      console.error('Failed to complete quest:', error)
      alert('Failed to complete quest')
    }
    setIsCompletingQuest(false)
  }

  // Handle partner quest creation
  const handleCreatePartnerQuest = async () => {
    if (!newQuestTitle.trim() || !selectedPartner) return

    setIsCreatingQuest(true)
    try {
      const userId = getCurrentUserId()
      const partnerId = selectedPartner.partnerId

      await createPartnerQuest({
        partnershipId: selectedPartner.id,
        assignedBy: userId,
        assignedTo: partnerId,
        title: newQuestTitle.trim(),
        description: newQuestDescription.trim() || null,
        emoji: newQuestEmoji,
        xpReward: newQuestXp,
        affectionReward: Math.floor(newQuestXp / 2.5),
        customReward: newQuestReward.trim() || null,
        stake: newQuestStake.trim() || null,
        dueAt: newQuestDueDate ? new Date(newQuestDueDate).toISOString() : null,
      })

      playSuccess()
      // Reset and close
      setShowCreateQuest(false)
      setNewQuestTitle('')
      setNewQuestDescription('')
      setNewQuestEmoji('⭐')
      setNewQuestXp(25)
      setNewQuestReward('')
      setNewQuestStake('')
      setNewQuestDueDate('')
      setShowQuestOptions(false)
      // Refresh quests
      await fetchPartnerQuests()
    } catch (error) {
      console.error('Failed to create quest:', error)
      alert('Failed to create quest')
    }
    setIsCreatingQuest(false)
  }

  // Handle verifying a quest
  const handleVerifyQuest = async (questId: string) => {
    await updateQuestStatus(questId, 'verified')
    playSuccess()
    await fetchPartnerQuests()
  }

  return (
    <PageContainer>
      {/* Unread Emotes Floating Badge */}
      <AnimatePresence>
        {unreadEmotes > 0 && (
          <motion.button
            initial={{ opacity: 0, scale: 0.5, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.5, y: -20 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/partners')}
            className="fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary to-pink-500 text-white rounded-full shadow-lg shadow-primary/30"
          >
            <MessageCircle className="w-5 h-5" />
            <span className="font-bold">{unreadEmotes}</span>
            <span className="text-sm">new</span>
            <motion.span
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
              className="absolute -top-1 -right-1 w-3 h-3 bg-white rounded-full"
            />
          </motion.button>
        )}
      </AnimatePresence>

      {/* View Mode Toggle - Only show if in social mode with partners */}
      {waifuMode === 'social' && partnerships.length > 0 && (
        <div className="flex justify-center mb-4">
          <div className="flex bg-bg-surface rounded-xl p-1 gap-1">
            <button
              onClick={() => setViewMode('solo')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                viewMode === 'solo'
                  ? 'bg-primary text-white shadow-md'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              <User className="w-4 h-4" />
              My Quests
            </button>
            <button
              onClick={() => setViewMode('partner')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                viewMode === 'partner'
                  ? 'bg-secondary text-white shadow-md'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              <Users className="w-4 h-4" />
              Partner
              {activeQuestsForMe.length > 0 && (
                <span className="w-5 h-5 rounded-full bg-white/20 text-xs flex items-center justify-center">
                  {activeQuestsForMe.length}
                </span>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Flip View Container */}
      <AnimatePresence mode="wait">
        {viewMode === 'solo' ? (
          <motion.div
            key="solo-view"
            initial={{ opacity: 0, rotateY: -90 }}
            animate={{ opacity: 1, rotateY: 0 }}
            exit={{ opacity: 0, rotateY: 90 }}
            transition={{ duration: 0.3 }}
          >
            {/* Character Display */}
            <CharacterDisplay showStats size="md" />

            {/* Streak banner */}
            {streakDays > 0 && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="mt-4 flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-accent/20 to-transparent rounded-xl border border-accent/30"
              >
                <Trophy className="text-accent" size={24} />
                <div>
                  <p className="font-bold text-accent">{streakDays} Day Streak!</p>
                  <p className="text-xs text-text-secondary">Keep it up!</p>
                </div>
              </motion.div>
            )}

            {/* Section header */}
            <div className="flex items-center justify-between mt-6 mb-3">
              <h2 className="font-display font-bold text-lg flex items-center gap-2">
                <Calendar size={20} className="text-primary" />
                Today's Quests
              </h2>
              <span className="text-sm text-text-secondary">
                {todaysTasks.length} pending
              </span>
            </div>

            {/* Task list */}
            <div className="space-y-3">
              <AnimatePresence mode="popLayout">
                {todaysTasks.length === 0 ? (
                  <EmptyState
                    {...EMPTY_STATES.quests}
                    variant="compact"
                    actionLabel="Add Quest"
                    onAction={() => setShowAddTask(true)}
                  />
                ) : (
                  todaysTasks.map((task, index) => (
                    <motion.div
                      key={task.id}
                      layout
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: -100 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Card hover glow="pink" className="flex items-start gap-3">
                        {/* Checkbox */}
                        <button
                          onClick={() => handleCompleteTask(task.id)}
                          className="mt-1 w-7 h-7 rounded-lg border-2 border-text-secondary hover:border-primary hover:bg-primary/10 transition-colors flex-shrink-0"
                        />

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold truncate">{task.title}</h3>
                          <div className="flex items-center gap-3 mt-1 text-sm text-text-secondary">
                            <span className={getDifficultyColor(task.difficulty)}>
                              {getDifficultyIcon(task.difficulty)} {task.difficulty}
                            </span>
                            {task.dueDate && (
                              <span className="truncate">
                                Due: {new Date(task.dueDate).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* XP reward */}
                        <div className="px-3 py-1.5 bg-gradient-to-r from-primary to-secondary rounded-xl text-sm font-bold flex-shrink-0">
                          +{task.xpReward} XP
                        </div>
                      </Card>
                    </motion.div>
                  ))
                )}
              </AnimatePresence>
            </div>

            {/* Add task button */}
            <motion.button
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowAddTask(true)}
              className="mt-4 w-full py-4 border-2 border-dashed border-text-secondary rounded-2xl text-text-secondary hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-2 font-semibold"
            >
              <Plus size={20} />
              Add New Quest
            </motion.button>
          </motion.div>
        ) : (
          /* Partner View */
          <motion.div
            key="partner-view"
            initial={{ opacity: 0, rotateY: 90 }}
            animate={{ opacity: 1, rotateY: 0 }}
            exit={{ opacity: 0, rotateY: -90 }}
            transition={{ duration: 0.3 }}
          >
            {/* Partner selector if multiple partners */}
            {partnerships.length > 1 && (
              <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
                {partnerships.map((partner) => (
                  <button
                    key={partner.id}
                    onClick={() => setSelectedPartner(partner)}
                    className={`flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-xl transition-all ${
                      selectedPartner?.id === partner.id
                        ? 'bg-secondary text-white'
                        : 'bg-bg-surface text-text-secondary hover:bg-bg-card'
                    }`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center overflow-hidden">
                      {partner.partnerAvatarUrl ? (
                        <img
                          src={partner.partnerAvatarUrl}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User className="w-4 h-4 text-text-muted" />
                      )}
                    </div>
                    <span className="text-sm font-medium">
                      {partner.myNicknameForThem || partner.partnerDisplayName || partner.partnerUsername}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {/* Partner Display (instead of waifu) */}
            {selectedPartner && (
              <div className="text-center mb-6">
                {/* Partner avatar */}
                <div className="relative inline-block">
                  <motion.div
                    className="w-32 h-32 mx-auto rounded-2xl bg-gradient-to-br from-secondary/20 to-primary/20 flex items-center justify-center overflow-hidden border-4 border-secondary/30"
                    animate={{ scale: [1, 1.02, 1] }}
                    transition={{ repeat: Infinity, duration: 3 }}
                  >
                    {selectedPartner.partnerAvatarUrl ? (
                      <img
                        src={selectedPartner.partnerAvatarUrl}
                        alt={selectedPartner.partnerDisplayName || 'Partner'}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User className="w-16 h-16 text-text-muted" />
                    )}
                  </motion.div>
                  {/* Online indicator */}
                  <div
                    className={`absolute bottom-1 right-1 w-5 h-5 rounded-full border-3 border-bg-card ${
                      selectedPartner.isOnline ? 'bg-success' : 'bg-text-muted'
                    }`}
                  />
                </div>

                {/* Partner name */}
                <h2 className="font-display text-xl font-bold mt-3">
                  {selectedPartner.myNicknameForThem || selectedPartner.partnerDisplayName || selectedPartner.partnerUsername}
                </h2>

                {/* Affection level */}
                <div className="flex items-center justify-center gap-2 mt-1">
                  <span className="text-lg">
                    {getAffectionLevel(selectedPartner.myAffection || 0).emoji}
                  </span>
                  <span className={`text-sm font-medium ${getAffectionLevel(selectedPartner.myAffection || 0).color}`}>
                    {getAffectionLevel(selectedPartner.myAffection || 0).title}
                  </span>
                </div>

                {/* Affection progress bar */}
                <div className="max-w-xs mx-auto mt-2">
                  <div className="h-2 bg-bg-surface rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${getAffectionLevel(selectedPartner.myAffection || 0).progress}%` }}
                      className="h-full bg-gradient-to-r from-secondary to-pink-500"
                    />
                  </div>
                  <p className="text-xs text-text-muted mt-1">
                    {formatAffection(selectedPartner.myAffection || 0)} affection
                  </p>
                </div>
              </div>
            )}

            {/* Partner Quests Section */}
            <div className="flex items-center justify-between mt-4 mb-3">
              <h2 className="font-display font-bold text-lg flex items-center gap-2">
                <Swords size={20} className="text-secondary" />
                Quests from Partner
              </h2>
              <span className="text-sm text-text-secondary">
                {activeQuestsForMe.length} active
              </span>
            </div>

            {/* Partner Quest list */}
            <div className="space-y-3">
              <AnimatePresence mode="popLayout">
                {activeQuestsForMe.length === 0 ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center py-8"
                  >
                    <Swords className="w-10 h-10 mx-auto text-text-muted mb-2" />
                    <p className="text-text-secondary">No quests from your partner yet</p>
                    <p className="text-text-muted text-sm mt-1">
                      They can assign quests to challenge you!
                    </p>
                  </motion.div>
                ) : (
                  activeQuestsForMe.map((quest, index) => (
                    <motion.div
                      key={quest.id}
                      layout
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: -100 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Card hover glow="purple" className="flex items-start gap-3">
                        {/* Quest emoji */}
                        <div className="w-12 h-12 rounded-xl bg-bg-surface flex items-center justify-center text-2xl flex-shrink-0">
                          {quest.emoji || '⭐'}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold truncate">{quest.title}</h3>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              quest.status === 'pending'
                                ? 'bg-warning/20 text-warning'
                                : 'bg-primary/20 text-primary'
                            }`}>
                              {quest.status === 'pending' ? 'New' : 'In Progress'}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-sm text-text-secondary">
                            <span className="flex items-center gap-1">
                              <Trophy className="w-3 h-3" />
                              {quest.xpReward} XP
                            </span>
                            <span className="flex items-center gap-1">
                              <Heart className="w-3 h-3" />
                              +{quest.affectionReward}
                            </span>
                            {quest.dueAt && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatDistanceToNow(quest.dueAt)}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Action button */}
                        {quest.status === 'pending' ? (
                          <Button
                            size="sm"
                            onClick={async () => {
                              await updateQuestStatus(quest.id, 'accepted')
                              playSuccess()
                            }}
                          >
                            <Check className="w-4 h-4" />
                            Accept
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            className="bg-gradient-to-r from-secondary to-primary"
                            onClick={() => setQuestToComplete(quest)}
                          >
                            Done!
                          </Button>
                        )}
                      </Card>
                    </motion.div>
                  ))
                )}
              </AnimatePresence>
            </div>

            {/* Assign quest button */}
            <motion.button
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowCreateQuest(true)}
              className="mt-4 w-full py-4 border-2 border-dashed border-secondary/50 rounded-2xl text-secondary hover:border-secondary hover:bg-secondary/5 transition-colors flex items-center justify-center gap-2 font-semibold"
            >
              <Plus size={20} />
              Assign Quest to Partner
            </motion.button>

            {/* Quests pending your verification */}
            {pendingVerification.length > 0 && (
              <div className="mt-6">
                <h3 className="font-display font-bold text-sm text-text-secondary mb-2 flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  Waiting for your approval
                </h3>
                <div className="space-y-2">
                  {pendingVerification.map((quest) => (
                    <Card key={quest.id} className="flex items-center gap-3 py-2">
                      <span className="text-xl">{quest.emoji || '⭐'}</span>
                      <span className="flex-1 truncate text-sm">{quest.title}</span>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleVerifyQuest(quest.id)}
                      >
                        <Check className="w-4 h-4" />
                        Verify
                      </Button>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Task Completion Modal */}
      <Modal
        isOpen={completionModal.show}
        onClose={handleCompletionClose}
        showCloseButton={false}
      >
        <div className="text-center py-4">
          <motion.h2
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="font-display font-bold text-2xl text-gradient mb-4"
          >
            ✨ QUEST COMPLETE! ✨
          </motion.h2>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="w-40 h-52 mx-auto mb-4 bg-gradient-to-b from-bg-surface to-bg-dark rounded-2xl flex items-center justify-center animate-bounce-soft"
          >
            <span className="text-6xl">🎉💕</span>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-text-secondary mb-4"
          >
            "{CHARACTER_DIALOGUES[activeCharacterId].taskComplete[0]}"
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="flex justify-center gap-6 mb-6"
          >
            <div className="flex items-center gap-2 text-accent font-bold text-lg">
              <Zap size={20} />
              +{completionModal.xpGained} XP
            </div>
            <div className="flex items-center gap-2 text-red-400 font-bold text-lg">
              ❤️ +{Math.floor(completionModal.xpGained / 5)} Affection
            </div>
          </motion.div>

          {/* Milestone hint */}
          {pendingMilestone?.milestoneCrossed && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-sm text-primary mb-4 animate-pulse"
            >
              ✨ Milestone reached! Something special awaits... ✨
            </motion.p>
          )}

          <Button onClick={handleCompletionClose} size="lg">
            Continue ▶
          </Button>
        </div>
      </Modal>

      {/* Milestone Celebration Modal */}
      {milestoneModal.milestone && (
        <MilestoneCelebration
          isOpen={milestoneModal.show}
          onClose={() => setMilestoneModal({ show: false, milestone: null, gemReward: 0, affectionBonus: 0, newLevel: undefined })}
          milestone={milestoneModal.milestone}
          characterId={activeCharacterId}
          gemReward={milestoneModal.gemReward}
          affectionBonus={milestoneModal.affectionBonus}
          newLevel={milestoneModal.newLevel}
        />
      )}

      {/* Add Task Modal */}
      <Modal
        isOpen={showAddTask}
        onClose={() => setShowAddTask(false)}
        title="New Quest"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-text-secondary mb-2">
              Quest Name
            </label>
            <input
              type="text"
              value={newTaskTitle}
              onChange={(e) => setNewTaskTitle(e.target.value)}
              placeholder="What needs to be done?"
              className="w-full px-4 py-3 bg-bg-dark rounded-xl border border-transparent focus:border-primary focus:outline-none transition-colors"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm text-text-secondary mb-2">
              Difficulty
            </label>
            <div className="grid grid-cols-4 gap-2">
              {(['easy', 'medium', 'hard', 'boss'] as Difficulty[]).map((diff) => (
                <button
                  key={diff}
                  onClick={() => setNewTaskDifficulty(diff)}
                  className={`p-3 rounded-xl text-center transition-all ${
                    newTaskDifficulty === diff
                      ? 'bg-primary/20 border-2 border-primary'
                      : 'bg-bg-dark border-2 border-transparent hover:border-primary/30'
                  }`}
                >
                  <span className="text-xl">{getDifficultyIcon(diff)}</span>
                  <span className={`block text-xs mt-1 capitalize ${getDifficultyColor(diff)}`}>
                    {diff}
                  </span>
                </button>
              ))}
            </div>
            <p className="text-xs text-text-secondary mt-2">
              Reward: {DIFFICULTY_XP[newTaskDifficulty].min}-{DIFFICULTY_XP[newTaskDifficulty].max} XP
            </p>
          </div>

          {/* More Options Toggle */}
          <button
            onClick={() => setShowMoreOptions(!showMoreOptions)}
            className="w-full flex items-center justify-center gap-2 py-2 text-sm text-text-secondary hover:text-primary transition-colors"
          >
            <span>{showMoreOptions ? 'Less options' : 'More options'}</span>
            <ChevronDown size={16} className={`transition-transform ${showMoreOptions ? 'rotate-180' : ''}`} />
          </button>

          {/* Expanded Options */}
          <AnimatePresence>
            {showMoreOptions && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="space-y-4 pt-2 border-t border-white/10">
                  {/* Description */}
                  <div>
                    <label className="block text-sm text-text-secondary mb-2">
                      Description
                    </label>
                    <textarea
                      value={newDescription}
                      onChange={(e) => setNewDescription(e.target.value)}
                      placeholder="Add more details..."
                      rows={2}
                      className="w-full px-4 py-3 bg-bg-dark rounded-xl border border-transparent focus:border-primary focus:outline-none transition-colors resize-none text-sm"
                    />
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-sm text-text-secondary mb-2">
                      Category
                    </label>
                    <div className="grid grid-cols-4 gap-1.5">
                      {TASK_CATEGORIES.map((cat) => (
                        <button
                          key={cat.id}
                          onClick={() => setNewCategory(newCategory === cat.id ? null : cat.id)}
                          className={`p-1.5 rounded-lg text-center transition-all ${
                            newCategory === cat.id
                              ? `${cat.color} ring-1 ring-current`
                              : 'bg-bg-dark hover:bg-bg-surface'
                          }`}
                        >
                          <span className="text-sm block">{cat.icon}</span>
                          <span className="text-[10px] block truncate">{cat.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Recurring toggle */}
                  <button
                    onClick={() => setIsRecurring(!isRecurring)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
                      isRecurring
                        ? 'bg-secondary/20 border border-secondary'
                        : 'bg-bg-dark border border-transparent hover:border-secondary/30'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Repeat size={18} className={isRecurring ? 'text-secondary' : 'text-text-muted'} />
                      <span className={`text-sm ${isRecurring ? 'text-secondary font-medium' : 'text-text-secondary'}`}>
                        Recurring Quest
                      </span>
                    </div>
                    <div className={`w-9 h-5 rounded-full transition-colors ${isRecurring ? 'bg-secondary' : 'bg-bg-elevated'}`}>
                      <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform mt-0.5 ${isRecurring ? 'translate-x-4 ml-0.5' : 'translate-x-0.5'}`} />
                    </div>
                  </button>

                  {/* Recurrence options */}
                  <AnimatePresence>
                    {isRecurring && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="space-y-3">
                          {/* Recurrence type */}
                          <div className="grid grid-cols-4 gap-1.5">
                            {([
                              { type: 'daily' as RecurrenceType, label: 'Daily', icon: '☀️', color: 'bg-secondary' },
                              { type: 'weekly' as RecurrenceType, label: 'Weekly', icon: '📅', color: 'bg-cyan-500' },
                              { type: 'monthly' as RecurrenceType, label: 'Monthly', icon: '🗓️', color: 'bg-purple-500' },
                              { type: 'yearly' as RecurrenceType, label: 'Yearly', icon: '🎂', color: 'bg-amber-500' },
                            ]).map((option) => (
                              <button
                                key={option.type}
                                onClick={() => setRecurrenceType(option.type)}
                                className={`py-1.5 px-1 rounded-lg text-xs font-medium transition-all flex flex-col items-center gap-0.5 ${
                                  recurrenceType === option.type
                                    ? `${option.color} text-white`
                                    : 'bg-bg-dark hover:bg-bg-surface'
                                }`}
                              >
                                <span className="text-sm">{option.icon}</span>
                                <span>{option.label}</span>
                              </button>
                            ))}
                          </div>

                          {/* Weekly day selection */}
                          {recurrenceType === 'weekly' && (
                            <div className="flex gap-1">
                              {WEEKDAYS.map((day) => (
                                <button
                                  key={day.value}
                                  onClick={() => {
                                    setWeeklyDays(prev =>
                                      prev.includes(day.value)
                                        ? prev.filter(d => d !== day.value)
                                        : [...prev, day.value]
                                    )
                                  }}
                                  className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                    weeklyDays.includes(day.value)
                                      ? 'bg-cyan-500 text-white'
                                      : 'bg-bg-dark hover:bg-bg-surface'
                                  }`}
                                >
                                  {day.label}
                                </button>
                              ))}
                            </div>
                          )}

                          {/* Monthly day selection */}
                          {recurrenceType === 'monthly' && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-text-muted">Day:</span>
                              <select
                                value={monthlyDay}
                                onChange={(e) => setMonthlyDay(Number(e.target.value))}
                                className="flex-1 px-2 py-1.5 bg-bg-dark rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                              >
                                {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                                  <option key={day} value={day}>{day}</option>
                                ))}
                              </select>
                            </div>
                          )}

                          {/* Yearly month + day selection */}
                          {recurrenceType === 'yearly' && (
                            <div className="flex gap-2">
                              <select
                                value={yearlyMonth}
                                onChange={(e) => setYearlyMonth(Number(e.target.value))}
                                className="flex-1 px-2 py-1.5 bg-bg-dark rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-amber-500"
                              >
                                {MONTHS.map((month) => (
                                  <option key={month.value} value={month.value}>{month.label}</option>
                                ))}
                              </select>
                              <select
                                value={monthlyDay}
                                onChange={(e) => setMonthlyDay(Number(e.target.value))}
                                className="w-16 px-2 py-1.5 bg-bg-dark rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-amber-500"
                              >
                                {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                                  <option key={day} value={day}>{day}</option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Due date & time - only show if not recurring */}
                  {!isRecurring && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-text-muted mb-1">Due Date</label>
                        <input
                          type="date"
                          value={newDueDate}
                          onChange={(e) => setNewDueDate(e.target.value)}
                          className="w-full px-3 py-2 bg-bg-dark rounded-lg border border-transparent focus:border-primary focus:outline-none transition-colors text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-text-muted mb-1">Due Time</label>
                        <input
                          type="time"
                          value={newDueTime}
                          onChange={(e) => setNewDueTime(e.target.value)}
                          className="w-full px-3 py-2 bg-bg-dark rounded-lg border border-transparent focus:border-primary focus:outline-none transition-colors text-sm"
                        />
                      </div>
                    </div>
                  )}

                  {/* Time for recurring tasks */}
                  {isRecurring && (
                    <div>
                      <label className="block text-xs text-text-muted mb-1">Reminder Time</label>
                      <input
                        type="time"
                        value={newDueTime}
                        onChange={(e) => setNewDueTime(e.target.value)}
                        className="w-full px-3 py-2 bg-bg-dark rounded-lg border border-transparent focus:border-secondary focus:outline-none transition-colors text-sm"
                      />
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex gap-3 pt-2">
            <Button variant="ghost" onClick={() => setShowAddTask(false)} fullWidth>
              Cancel
            </Button>
            <Button
              onClick={handleAddTask}
              fullWidth
              disabled={!newTaskTitle.trim() || (isRecurring && recurrenceType === 'weekly' && weeklyDays.length === 0)}
            >
              Create Quest
            </Button>
          </div>
        </div>
      </Modal>

      {/* Partner Quest Completion Modal */}
      <Modal
        isOpen={!!questToComplete}
        onClose={() => {
          setQuestToComplete(null)
          setProofFile(null)
          setProofPreview(null)
        }}
        title="Complete Quest"
      >
        {questToComplete && (
          <div className="space-y-4">
            {/* Quest info */}
            <div className="flex items-center gap-3 p-3 bg-bg-surface rounded-xl">
              <span className="text-3xl">{questToComplete.emoji || '⭐'}</span>
              <div>
                <h3 className="font-semibold">{questToComplete.title}</h3>
                <p className="text-sm text-text-secondary">
                  +{questToComplete.xpReward} XP • +{questToComplete.affectionReward} 💕
                </p>
              </div>
            </div>

            {/* Proof upload (optional) */}
            <div>
              <label className="block text-sm text-text-secondary mb-2">
                Add proof (optional)
              </label>
              <input
                ref={proofInputRef}
                type="file"
                accept="image/*,video/*"
                onChange={handleProofSelect}
                className="hidden"
              />

              {proofPreview ? (
                <div className="relative">
                  {proofFile?.type.startsWith('video/') ? (
                    <video
                      src={proofPreview}
                      className="w-full h-48 object-cover rounded-xl"
                      controls
                    />
                  ) : (
                    <img
                      src={proofPreview}
                      alt="Proof"
                      className="w-full h-48 object-cover rounded-xl"
                    />
                  )}
                  <button
                    onClick={() => {
                      setProofFile(null)
                      setProofPreview(null)
                    }}
                    className="absolute top-2 right-2 p-1 bg-bg-dark/80 rounded-full"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => proofInputRef.current?.click()}
                  className="w-full py-8 border-2 border-dashed border-text-muted rounded-xl text-text-muted hover:border-secondary hover:text-secondary transition-colors flex flex-col items-center gap-2"
                >
                  <Camera className="w-8 h-8" />
                  <span className="text-sm">Tap to add photo/video proof</span>
                </button>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                variant="ghost"
                onClick={() => {
                  setQuestToComplete(null)
                  setProofFile(null)
                  setProofPreview(null)
                }}
                fullWidth
              >
                Cancel
              </Button>
              <Button
                onClick={handleCompletePartnerQuest}
                fullWidth
                disabled={isCompletingQuest}
                className="bg-gradient-to-r from-secondary to-primary"
              >
                {isCompletingQuest ? 'Completing...' : 'Mark Complete'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Create Partner Quest Modal */}
      <Modal
        isOpen={showCreateQuest}
        onClose={() => {
          setShowCreateQuest(false)
          setNewQuestTitle('')
          setNewQuestDescription('')
          setNewQuestEmoji('⭐')
          setNewQuestXp(25)
          setNewQuestReward('')
          setNewQuestStake('')
          setNewQuestDueDate('')
          setShowQuestOptions(false)
        }}
        title={`Quest for ${selectedPartner?.myNicknameForThem || selectedPartner?.partnerDisplayName || 'Partner'}`}
      >
        <div className="space-y-4">
          {/* Title with emoji */}
          <div className="flex gap-2">
            <button
              onClick={() => {
                const currentIdx = QUEST_EMOJIS.indexOf(newQuestEmoji)
                const nextIdx = (currentIdx + 1) % QUEST_EMOJIS.length
                setNewQuestEmoji(QUEST_EMOJIS[nextIdx])
              }}
              className="w-14 h-14 rounded-xl bg-bg-surface flex items-center justify-center text-3xl hover:bg-bg-card transition-colors flex-shrink-0"
            >
              {newQuestEmoji}
            </button>
            <input
              type="text"
              value={newQuestTitle}
              onChange={(e) => setNewQuestTitle(e.target.value)}
              placeholder="Quest title..."
              className="flex-1 px-4 py-3 bg-bg-dark rounded-xl border border-transparent focus:border-secondary focus:outline-none transition-colors text-lg font-medium"
              autoFocus
              maxLength={100}
            />
          </div>

          {/* Custom reward with quick options */}
          <div>
            <label className="block text-sm text-text-secondary mb-2">
              Reward (optional)
            </label>
            <input
              type="text"
              value={newQuestReward}
              onChange={(e) => setNewQuestReward(e.target.value)}
              placeholder="What will they get?"
              className="w-full px-4 py-3 bg-bg-dark rounded-xl border border-transparent focus:border-secondary focus:outline-none transition-colors"
              maxLength={100}
            />
            <div className="flex flex-wrap gap-1.5 mt-2">
              {QUICK_REWARDS.map((reward) => (
                <button
                  key={reward.label}
                  onClick={() => setNewQuestReward(`${reward.emoji} ${reward.label}`)}
                  className="text-xs px-2 py-1 rounded-full bg-bg-surface hover:bg-secondary/20 transition-colors"
                >
                  {reward.emoji} {reward.label}
                </button>
              ))}
            </div>
          </div>

          {/* More Options Accordion */}
          <button
            onClick={() => setShowQuestOptions(!showQuestOptions)}
            className="w-full flex items-center justify-center gap-2 py-2 text-sm text-text-secondary hover:text-secondary transition-colors"
          >
            <span>{showQuestOptions ? 'Less options' : 'More options'}</span>
            <ChevronDown size={16} className={`transition-transform ${showQuestOptions ? 'rotate-180' : ''}`} />
          </button>

          <AnimatePresence>
            {showQuestOptions && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="space-y-4 pt-2 border-t border-white/10">
                  {/* Description */}
                  <div>
                    <label className="block text-sm text-text-secondary mb-2">
                      Description
                    </label>
                    <textarea
                      value={newQuestDescription}
                      onChange={(e) => setNewQuestDescription(e.target.value)}
                      placeholder="More details about the quest..."
                      rows={2}
                      className="w-full px-4 py-3 bg-bg-dark rounded-xl border border-transparent focus:border-secondary focus:outline-none transition-colors resize-none text-sm"
                      maxLength={500}
                    />
                  </div>

                  {/* XP Reward slider */}
                  <div>
                    <label className="block text-sm text-text-secondary mb-2">
                      <Zap className="w-4 h-4 inline mr-1 text-accent" />
                      XP Reward: {newQuestXp} (+{Math.floor(newQuestXp / 2.5)} 💕)
                    </label>
                    <input
                      type="range"
                      min={10}
                      max={200}
                      step={5}
                      value={newQuestXp}
                      onChange={(e) => setNewQuestXp(Number(e.target.value))}
                      className="w-full accent-secondary"
                    />
                    <div className="flex justify-between text-xs text-text-muted mt-1">
                      <span>Easy</span>
                      <span>Medium</span>
                      <span>Hard</span>
                      <span>Epic</span>
                    </div>
                  </div>

                  {/* Stakes */}
                  <div>
                    <label className="block text-sm text-text-secondary mb-2">
                      <Swords className="w-4 h-4 inline mr-1 text-secondary" />
                      Stakes (optional)
                    </label>
                    <input
                      type="text"
                      value={newQuestStake}
                      onChange={(e) => setNewQuestStake(e.target.value)}
                      placeholder="What if they fail? (just for fun)"
                      className="w-full px-4 py-3 bg-bg-dark rounded-xl border border-transparent focus:border-secondary focus:outline-none transition-colors text-sm"
                      maxLength={100}
                    />
                  </div>

                  {/* Due Date */}
                  <div>
                    <label className="block text-sm text-text-secondary mb-2">
                      <Calendar className="w-4 h-4 inline mr-1" />
                      Due Date (optional)
                    </label>
                    <input
                      type="datetime-local"
                      value={newQuestDueDate}
                      onChange={(e) => setNewQuestDueDate(e.target.value)}
                      min={new Date().toISOString().slice(0, 16)}
                      className="w-full px-4 py-3 bg-bg-dark rounded-xl border border-transparent focus:border-secondary focus:outline-none transition-colors text-sm"
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="ghost"
              onClick={() => {
                setShowCreateQuest(false)
                setNewQuestTitle('')
                setNewQuestDescription('')
                setNewQuestEmoji('⭐')
                setNewQuestXp(25)
                setNewQuestReward('')
                setNewQuestStake('')
                setNewQuestDueDate('')
                setShowQuestOptions(false)
              }}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreatePartnerQuest}
              fullWidth
              disabled={!newQuestTitle.trim() || isCreatingQuest}
              className="bg-gradient-to-r from-secondary to-primary"
            >
              {isCreatingQuest ? 'Sending...' : 'Send Quest'}
            </Button>
          </div>
        </div>
      </Modal>
    </PageContainer>
  )
}
